// Shared template-based DataDisplay renderer.
// Applies a DataDisplayTemplate to a ResolvedDataDisplay using a RenderContext for field mapping.
import { DataDisplayTemplate, ResolvedDataDisplay } from './datadisplay.types'
import type { RenderContext } from '../render-kit'

/**
 * Convert a CCM snake_case or dot.notation field name to a camelCase alias.
 * Examples: process_path → processPath, src.process.name → srcProcessName
 * Exported so tool renderers and datasource renderers can share this utility.
 */
export function toCamelCase(s: string): string {
  return s
    .replace(/\./g, '_')
    .split('_')
    .map((p, i) => (i === 0 ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join('')
}

/**
 * Derive a display alias for an aggField.
 * Uses aggField.alias if set; otherwise builds camelCase from fn + ccm field.
 * Examples: { field: 'process_path', fn: 'values' } → 'valuesProcessPath'
 *           { field: 'hostname', fn: 'count' }       → 'countHostname'
 *           { field: 'hostname' }                     → 'hostname'
 */
function deriveAlias(
  ccmField: string,
  fn?: string,
  explicitAlias?: string,
): string {
  if (explicitAlias) return explicitAlias
  if (fn) return toCamelCase(`${fn}_${ccmField}`)
  return toCamelCase(ccmField)
}

/**
 * Render a DataDisplay spec using the provided tool template.
 * Returns an empty string if the template supports nothing applicable.
 */
export function renderDataDisplayFromTemplate(
  resolved: ResolvedDataDisplay,
  ctx: RenderContext,
  template: DataDisplayTemplate,
): string {
  const parts: string[] = []

  // Build aggregation clauses from aggFields
  const aggClauses: string[] = []

  if (template.mergeCollect !== undefined) {
    // Merge-collect mode: coalesce all values/collect fn fields into one collect([...]) — no alias.
    // fromOverride fields bypass fieldExists — tool-specific field names pass through via mapField's ?? c fallback.
    // Non-collect fns (count, min, max, etc.) are rendered per-field as normal.
    const collectMapped: string[] = []
    const otherFields = resolved.aggFields.filter((af) => {
      if (!af.fromOverride && !ctx.fieldExists(af.field)) return false
      if (af.fn === 'values' || af.fn === 'collect') {
        collectMapped.push(ctx.mapField(af.field))
        return false
      }
      return true
    })
    // Render non-collect aggFields per-field
    for (const af of otherFields) {
      const mappedField = ctx.mapField(af.field)
      const alias = deriveAlias(af.field, af.fn, af.alias)
      if (af.fn) {
        const renderer = template.aggFns[af.fn]
        if (renderer) aggClauses.push(renderer(mappedField, alias))
      } else {
        const clause = template.fieldAlias
          ? template.fieldAlias(mappedField, alias)
          : mappedField
        aggClauses.push(clause)
      }
    }
    // Emit one combined collect for all values/collect fields
    if (collectMapped.length) {
      const limitSuffix =
        template.mergeCollect.limit != null
          ? `, limit=${template.mergeCollect.limit}`
          : ''
      aggClauses.push(`collect([${collectMapped.join(', ')}]${limitSuffix})`)
    }
  } else {
    // Standard per-field rendering — skip fields with no CCM mapping unless fromOverride is set.
    // fromOverride fields (from explicit tool override aggFields) bypass fieldExists and pass through as-is.
    for (const af of resolved.aggFields.filter((af) => af.fromOverride || ctx.fieldExists(af.field))) {
      const mappedField = ctx.mapField(af.field)
      const alias = deriveAlias(af.field, af.fn, af.alias)
      if (af.fn) {
        const renderer = template.aggFns[af.fn]
        if (renderer) {
          aggClauses.push(renderer(mappedField, alias))
        }
        // silently skip unsupported functions
      } else {
        // raw field reference: use fieldAlias if available, otherwise just the mapped field
        const clause = template.fieldAlias
          ? template.fieldAlias(mappedField, alias)
          : mappedField
        aggClauses.push(clause)
      }
    }
  }

  // Keep the CCM names alongside the mapped ones so templates that need to
  // Eta-wrap the CCM name (e.g. QRadar SELECT aliasing) can do so without
  // reverse-engineering the mapping.
  const groupByCcm = resolved.groupBy.filter((f) => ctx.fieldExists(f))
  const mappedGroupBy = groupByCcm.map((f) => ctx.mapField(f))

  const projectionCcm = resolved.fields.filter((f) => ctx.fieldExists(f))
  const mappedFields = projectionCcm.map((f) => ctx.mapField(f))

  // Priority: timeBucket > groupBy > projection
  if (resolved.timeBucket && template.timeBucket) {
    const span = resolved.timeBucket.span ?? '1h'
    const clause = template.timeBucket(span, mappedGroupBy, aggClauses)
    if (clause) parts.push(clause)
  } else if (mappedGroupBy.length && template.groupBy) {
    // Default count clause if no aggFields specified
    const effectiveClauses =
      aggClauses.length === 0 && template.aggFns['count']
        ? [template.aggFns['count']!('', 'count')]
        : aggClauses
    const clause = template.groupBy(mappedGroupBy, effectiveClauses, groupByCcm)
    if (clause) parts.push(clause)
  } else if (mappedFields.length && template.projection) {
    const clause = template.projection(mappedFields, projectionCcm)
    if (clause) parts.push(clause)
  }

  // Append raw condition statement if present (tool-specific passthrough)
  if (resolved.conditionStatement) {
    parts.push(resolved.conditionStatement)
  }

  return parts.join('\n')
}
